"use client";

import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
export default function DomesticSpacesShowcase() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const numeral = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const features = [{
    index: "01",
    eyebrow: "Installation",
    title: "Precision Climate",
    titleAccent: "Installation",
    body: "Our certified engineers deliver seamless air conditioning installations tailored to your property's unique layout. We ensure optimal airflow and energy efficiency, providing a quiet, comfortable environment from day one.",
    cta: "View Installation Services",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_installation_tech.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_installation_tech.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_installation_tech.png-wvc-sizes",
      alt: "Professional technician installing a modern wall-mounted air conditioning unit"
    },
    imageLeft: true
  }, {
    index: "02",
    eyebrow: "Maintenance",
    title: "System Performance",
    titleAccent: "Maintenance",
    body: "Regular servicing is essential for longevity and air quality. Our comprehensive maintenance checks identify potential issues early, keeping your cooling systems running at peak performance throughout the summer months.",
    cta: "Book Maintenance",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_maintenance_check.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_maintenance_check.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_maintenance_check.png-wvc-sizes",
      alt: "Technician performing a detailed maintenance check on an outdoor condenser unit"
    },
    imageLeft: false
  }, {
    index: "03",
    eyebrow: "Solutions",
    title: "Commercial Climate",
    titleAccent: "Solutions",
    body: "We provide robust climate control systems for offices, retail spaces, and commercial buildings. Our bespoke solutions ensure a productive, comfortable atmosphere for your staff and clients all year round.",
    cta: "Explore Commercial Options",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_ac_system.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_ac_system.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_ac_system.png-wvc-sizes",
      alt: "Modern commercial office space with integrated ceiling-mounted air conditioning"
    },
    imageLeft: true
  }];
  return <section data-section-id="1697" id="domesticspacesshowcase" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      {}
      <div ref={numeral.ref} aria-hidden="true" className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 select-none" style={{
      transform: numeral.isVisible ? "translate(-50%, -40px)" : "translate(-50%, 0)",
      transition: "transform 1200ms cubic-bezier(0.22, 0.61, 0.36, 1)"
    }}>
        <span className="font-default italic leading-none text-transparent" style={{
        fontSize: "clamp(8rem, 16vw, 14rem)",
        WebkitTextStroke: "1px var(--primary)",
        opacity: 0.18
      }}>
          02
        </span>
      </div>

      <div className="relative mx-auto w-full max-w-[1320px] px-5 sm:px-8 md:px-14">
        {}
        <div ref={header.ref} className="relative z-10 mx-auto flex max-w-[720px] flex-col items-center text-center" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 720ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 720ms cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          {}
          <div className="flex items-center gap-3">
            <span className="font-default uppercase" style={{
            fontSize: "clamp(0.6875rem, 0.75vw, 0.8125rem)",
            letterSpacing: "0.18em",
            color: "var(--primary)",
            fontWeight: 600
          }}>
              § N° 02 — Coastline Standards
            </span>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="block h-[4px] w-[4px] rounded-md" style={{
            background: "var(--primary)"
          }} />
            <span className="block h-px w-14" style={{
            background: "var(--primary)"
          }} />
          </div>

          {}
          <h2 className="font-default mt-8" style={{
          fontSize: "clamp(2.25rem, 4.6vw, 4.25rem)",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 0.98
        }}>
            Climate control with{" "}
            <em className="not-italic" style={{
            fontStyle: "italic",
            color: "var(--primary)",
            fontWeight: 400
          }}>
              precision
            </em>
            .
          </h2>

          <p className="font-default mt-6 max-w-[560px]" style={{
          color: "var(--muted-foreground)",
          fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
          lineHeight: 1.55,
          fontWeight: 400
        }}>
            Three core services, one commitment. Coastline Air Conditioning Services provides expert climate solutions across Sussex and Surrey, ensuring your environment remains perfectly tempered.
          </p>
        </div>

        {}
        <div className="mt-20 flex flex-col gap-24 md:mt-28 md:gap-32">
          {features.map((feature, i) => <FeatureRow key={feature.index} data-index={i} feature={feature} delay={i * 80} />)}
        </div>

        {}
        <div className="mt-24 flex items-center justify-start">
          <span className="font-default" style={{
          fontSize: "11px",
          color: "var(--muted-foreground)",
          letterSpacing: "0.08em"
        }}>
            Serving Sussex & Surrey — Professional Climate Control
          </span>
        </div>
      </div>
    </section>;
}
type FeatureProps = {
  feature: {
    index: string;
    eyebrow: string;
    title: string;
    titleAccent: string;
    body: string;
    cta: string;
    image: {
      src: string;
      srcset: string;
      sizes: string;
      alt: string;
    };
    imageLeft: boolean;
  };
  delay: number;
  "data-index"?: number;
};
function FeatureRow({
  feature,
  delay,
  ...rest
}: FeatureProps) {
  const row = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={row.ref} data-index={rest["data-index"]} className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-12" style={{
    opacity: row.isVisible ? 1 : 0,
    transform: row.isVisible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 720ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms, transform 720ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`
  }}>
      {}
      <div className={`md:col-span-7 ${feature.imageLeft ? "md:order-1" : "md:order-2"}`}>
        <div className="group relative">
          {}
          <div className="relative overflow-hidden rounded-lg border transition-all duration-300 shadow-lg" style={{
          background: "var(--card)",
          borderColor: "var(--border)"
        }}>
            <div className="aspect-[4/5] w-full max-w-full overflow-hidden md:aspect-[5/4]">
              <img src={feature.image.src} data-wvc-srcset={feature.image.srcset} data-wvc-sizes={feature.image.sizes} alt={feature.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
            </div>

            {}
            <span className="pointer-events-none absolute left-3 top-3 font-default uppercase" style={{
            fontSize: "10px",
            color: "var(--primary)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Coastline
            </span>
            <span className="pointer-events-none absolute right-3 top-3 font-default uppercase" style={{
            fontSize: "10px",
            color: "var(--primary)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Certified
            </span>
            <span className="pointer-events-none absolute bottom-3 left-3 font-default uppercase" style={{
            fontSize: "10px",
            color: "var(--primary)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              § {feature.index}
            </span>
            <span className="pointer-events-none absolute bottom-3 right-3 font-default uppercase" style={{
            fontSize: "10px",
            color: "var(--primary)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Climate
            </span>
          </div>
        </div>
      </div>

      {}
      <div className={`md:col-span-5 ${feature.imageLeft ? "md:order-2" : "md:order-1"}`}>
        {}
        <div className="flex items-center gap-3">
          <span className="font-default uppercase" style={{
          fontSize: "11px",
          letterSpacing: "0.18em",
          color: "var(--primary)",
          fontWeight: 600
        }}>
            § N° {feature.index} — {feature.eyebrow}
          </span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="block h-[4px] w-[4px] rounded-md" style={{
          background: "var(--primary)"
        }} />
          <span className="block h-px w-14" style={{
          background: "var(--primary)"
        }} />
        </div>

        {}
        <h3 className="font-default mt-6" style={{
        fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)",
        fontWeight: 700,
        letterSpacing: "-0.01em",
        lineHeight: 1.12,
        color: "var(--foreground)"
      }}>
          {feature.title}{" "}
          <em className="not-italic" style={{
          fontStyle: "italic",
          color: "var(--primary)",
          fontWeight: 400
        }}>
            {feature.titleAccent}
          </em>
        </h3>

        {}
        <p className="font-default mt-5 max-w-[440px]" style={{
        color: "var(--muted-foreground)",
        fontSize: "clamp(0.9375rem, 1vw, 1rem)",
        lineHeight: 1.62,
        fontWeight: 400
      }}>
          {feature.body}
        </p>

        {}
        
      </div>
    </div>;
}